import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import { PROFILE_TYPES, PROFILE_TYPE_LABELS } from '@modules/search/constants'
import { BorderlessFilterAccordion, FilterBadge } from '@modules/search/index.styles'
import { ProfileType } from '@modules/search/types'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { AccordionDetails, Box, Typography } from '@mui/material'

import { FilterSectionAccordionSummary, FilterTitleBox, OuterFilterBox } from '../FiltersSection/index.styles'
import { ProfileTypeFilterItemWrapper, ProfileTypeFiltersContainer, StyledCheckbox, StyledFormControlLabel } from './index.styles'

export const ProfileTypeFilters = ({
  isAllProfilesEnabled,
  handleSetAllSearchableProfiles,
  handleSetSearchableProfiles,
  searchableProfilesMap,
  selectedFiltersByProfiles,
  accordionView,
}: {
  accordionView?: boolean
  handleSetAllSearchableProfiles: (value: boolean) => void
  isAllProfilesEnabled: boolean
  handleSetSearchableProfiles: (profileType: ProfileType, enabled: boolean) => void
  searchableProfilesMap: Record<string, boolean>
  selectedFiltersByProfiles?: Nullable<Record<string, unknown[]>>
}) => {
  const { t } = useTranslation()
  const [isFilterExpanded, setFilterExpanded] = useState(false)

  const onSetAllSearchableProfiles = useCallback(
    () => handleSetAllSearchableProfiles(!isAllProfilesEnabled),
    [isAllProfilesEnabled, handleSetAllSearchableProfiles],
  )

  const labelPlacement = accordionView ? 'end' : 'start'
  const content = (
    <>
      <ProfileTypeFilterItemWrapper>
        <StyledFormControlLabel
          labelPlacement={labelPlacement}
          control={
            <StyledCheckbox
              checked={isAllProfilesEnabled}
              onChange={onSetAllSearchableProfiles}
              color="primary"
              slotProps={{
                input: {
                  // @ts-expect-error test-id
                  'data-testid': 'all_profiles_checkbox',
                  id: 'top-all',
                },
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  onSetAllSearchableProfiles()
                }
              }}
            />
          }
          label={<Typography variant="body1">{t(translationKeys.SEARCH_FILTER_BY_ALL_CATEGORIES)}</Typography>}
        />
      </ProfileTypeFilterItemWrapper>

      {Object.values(PROFILE_TYPES).map(profileType => (
        <ProfileTypeFilterItemWrapper key={profileType}>
          <StyledFormControlLabel
            value={profileType}
            labelPlacement={labelPlacement}
            control={
              <StyledCheckbox
                checked={searchableProfilesMap[profileType]}
                onChange={() => handleSetSearchableProfiles(profileType, !searchableProfilesMap[profileType])}
                disabled={!selectedFiltersByProfiles}
                color="primary"
                inputProps={{
                  // @ts-expect-error test-id
                  'data-testid': `${profileType}_profile_checkbox`,
                  id: `top-${profileType.toLowerCase()}`,
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleSetSearchableProfiles(profileType, !searchableProfilesMap[profileType])
                  }
                }}
              />
            }
            label={<Typography variant="body1">{PROFILE_TYPE_LABELS[profileType]}</Typography>}
          />
        </ProfileTypeFilterItemWrapper>
      ))}
    </>
  )

  if (!accordionView) {
    return <ProfileTypeFiltersContainer>{content}</ProfileTypeFiltersContainer>
  }

  // mobile styled accordion
  return (
    <OuterFilterBox $active>
      <BorderlessFilterAccordion
        disableGutters
        expanded={isFilterExpanded}
        onChange={(_, expanded) => setFilterExpanded(expanded)}
      >
        <FilterSectionAccordionSummary
          sx={{ flexDirection: 'row' }}
          expandIcon={<ExpandMoreIcon />}
          id="profile-type-filters_section"
          data-testid="profile_filter_accordion_summary"
        >
          <FilterTitleBox>
            <Box display="flex" alignItems="center">
              <Typography variant="h2">{t(translationKeys.PROFILES_FILTER_TITLE)}</Typography>
              <FilterBadge variant="dot" color="primary" />
            </Box>
          </FilterTitleBox>
        </FilterSectionAccordionSummary>

        <AccordionDetails sx={{ padding: 0 }}>
          <ProfileTypeFiltersContainer>{content}</ProfileTypeFiltersContainer>
        </AccordionDetails>
      </BorderlessFilterAccordion>
    </OuterFilterBox>
  )
}
